/**
 * UI permission-keys for the `GLOBAL` route — **sidebar nav visibility only**.
 *
 * Typed mirror of the BE CSV (the BE is the source of truth). Route access and
 * onboarding live on the per-domain routes (see {@link AclRoute}); GLOBAL only
 * decides whether each sidebar item renders.
 *
 * Key format: `{module}:{object}`. Sidebar items use module `sidebar`.
 */
export const GlobalAclKeys = {
  /** Sidebar → Clients (`/clients`). */
  sidebarClients: 'sidebar:clients',
  /** Sidebar → Entities (`/entities`). */
  sidebarEntities: 'sidebar:entities',
  /** Sidebar → Workers (`/workforce`). */
  sidebarWorkers: 'sidebar:workers',
  /** Sidebar → People (`/people`). */
  sidebarPeople: 'sidebar:people',
  /** Sidebar → Vendors (`/providers`). */
  sidebarVendors: 'sidebar:vendors',
  /** Sidebar → Agencies (`/agencies`). */
  sidebarAgencies: 'sidebar:agencies',
  /** Sidebar → Engagements (`/engagements`). */
  sidebarEngagements: 'sidebar:engagements',
  /**
   * Sidebar → Invoices (`/invoices`).
   *
   * Seeded YES for ALL FIVE personas in `acl-svc/config/ui-permissions.csv` — Invoices is a
   * dedicated tab for everyone, including WORKER (read-only payslips). The key exists for
   * consistency with the other sidebar routes, not to restrict access; who sees WHAT is decided by
   * the page (`src/pages/Invoices/persona-config.ts`), not by this.
   *
   * ⚠️ Fail-closed: an environment that has not imported that CSV row shows the tab to nobody.
   */
  sidebarInvoices: 'sidebar:invoices',
  /** Time tracking — logs, timesheets, time off, leave policies, holiday calendars.
   *  ⚠️ FAIL-CLOSED like its siblings: until `GLOBAL,sidebar:time` exists in
   *  `acl-svc/config/ui-permissions.csv`, this tab is hidden from EVERYONE. Seed it via
   *  `/sync-ui-permissions` when `be`'s ACL surface lands. */
  sidebarTime: 'sidebar:time',
  /** Sidebar → Agent (`/agent`). */
  sidebarAgent: 'sidebar:agent',
  /**
   * Sidebar → Import Center (`/import-center`). Seeded YES for PLATFORM_ADMIN (SA) only in
   * `acl-svc/config/ui-permissions.csv`; widening it to agencies is a CSV change, not a code change.
   * The page itself is gated separately (`entities:onboard`) — this key only decides the tab.
   *
   * ⚠️ Fail-closed: an environment that has not imported that CSV row shows the tab to nobody.
   */
  sidebarImportCenter: 'sidebar:import-center',
  /** Sidebar → Integrations (`/integrations`). */
  sidebarIntegrations: 'sidebar:integrations',
} as const;

export type GlobalAclKey = (typeof GlobalAclKeys)[keyof typeof GlobalAclKeys];
